import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const BookReviewContainer = styled.div`
  padding: 6rem 1.5rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgb(var(--bg));
  min-height: 100vh;
  font-family: 'Arial', sans-serif;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 10% 0%, rgba(224,84,109,0.12), transparent 40%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #e0546d;
  text-align: center;
  margin-bottom: 1rem;
  line-height: 1.2;
`;

const Author = styled.p`
  font-size: 1.2rem;
  color: rgb(var(--fg) / 0.78);
  margin-bottom: 1rem;
  font-style: italic;
`;

const CoverImage = styled.img`
  width: 100%;
  max-width: 300px;
  max-height: 600px;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 20px 40px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.08);
`;

const DatePublished = styled.p`
  font-size: 1rem;
  color: rgb(var(--gray) / 0.55);
  margin-bottom: 2rem;
`;

const ContentSection = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-bottom: 2rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  padding: 2rem;
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #e0546d;
  border-bottom: 1px solid rgba(224, 84, 109, 0.3);
  padding-bottom: 0.5rem;
`;

const SectionContent = styled.div`
  font-size: 1rem;
  line-height: 1.8;
  color: rgb(var(--fg) / 0.82);
`;

const Quote = styled.blockquote`
  font-style: italic;
  margin: 1.5rem 0;
  padding: 1rem;
  background: rgb(var(--fg) / 0.04);
  border-left: 3px solid #e0546d;
  border-radius: 8px;
  color: rgb(var(--fg) / 0.78);
`;

const ListItem = styled.li`
  margin-bottom: 1rem;
  font-size: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

const OrderedList = styled.ol`
  padding-left: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1200px;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  border: 1px solid rgba(224, 84, 109, 0.3);
  color: rgb(var(--fg) / 0.9);
  padding: 0.4rem 0.7rem;
  border-radius: 12px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
`;

const Button = styled.button`
  display: inline-block;
  margin: 2rem 0;
  background: rgb(var(--fg) / 0.06);
  color: rgb(var(--fg) / 0.92);
  border: 1px solid rgb(var(--fg) / 0.14);
  padding: 0.9rem 2rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: pointer;
  backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(224, 84, 109, 0.5);
  }
`;

export const TalkingToStrangersMetadata = {
  title: "Talking to Strangers",
  description: "What We Should Know about the People We Don’t Know",
  tags: ['Psychology', 'Communication', 'Bias', 'Human Behavior'],
  author: "Malcolm Gladwell",
  datePublished: "2023-08-23",
  category: "Book Review",
  path: "/book-review/talking-to-strangers",
  coverImage: "/images/talking_to_strangers.jpg",
};

const TalkingToStrangers = () => {
  const navigate = useNavigate();
  const metadata = TalkingToStrangersMetadata;

  return (
    <BookReviewContainer>
      <Title>{metadata.title}</Title>
      <Author>by {metadata.author}</Author>
      <CoverImage src={metadata.coverImage} alt={metadata.title} />
      <DatePublished>Date Published: {metadata.datePublished}</DatePublished>
      <TagContainer>
        {metadata.tags.map((tag, index) => (
          <Tag key={index}>{tag}</Tag>
        ))}
      </TagContainer>
      <br />
      <br />
      <ContentSection>
        <SectionTitle>Summary</SectionTitle>
        <SectionContent>
          <p>
            "Talking to Strangers" explores the miscommunication and misunderstandings we often face when interacting with people we don’t know. Malcolm Gladwell presents real-world cases, offering insights into how our assumptions about trust, truth, and behavior can lead to severe consequences. Through stories about infamous events, he questions the systems we use to judge others and how often they fail.
          </p>
          <br />
          <p>
            Gladwell dissects human behavior, using psychological research to explain why we are not as good at reading others as we think. He examines moments where our biases and misinterpretations have led to disasters in personal and societal interactions.
          </p>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Key Themes or Concepts</SectionTitle>
        <SectionContent>
          <OrderedList>
            <ListItem><strong>Misperceptions:</strong> The book emphasizes how we often fail to accurately understand people we don't know.</ListItem>
            <ListItem><strong>Trust and Deception:</strong> Gladwell delves into the psychology of trust and why we struggle to spot deception.</ListItem>
            <ListItem><strong>Judgment Errors:</strong> He examines how errors in judgment can lead to devastating consequences.</ListItem>
            <ListItem><strong>Truth Default Theory:</strong> Explores why humans are predisposed to believe others by default, even when deception is possible.</ListItem>
            <ListItem><strong>Bias and Prejudice:</strong> The role of unconscious bias in distorting our understanding of others.</ListItem>
          </OrderedList>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Favorite Quotes</SectionTitle>
        <SectionContent>
          <Quote>
            "The right way to talk to strangers is with caution and humility."
          </Quote>
          <Quote>
            "We have strategies for dealing with strangers that are deeply flawed."
          </Quote>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Personal Reflection</SectionTitle>
        <SectionContent>
          <p>
            "Talking to Strangers" made me rethink how I approach interactions with people I don’t know. It’s easy to trust in our ability to judge character, but Gladwell’s exploration of historical events and psychological studies exposes how often we get it wrong.
          </p>
          <br />
          <p>
            The book made me more aware of my own biases and how quickly I can misinterpret others. It reinforces the importance of being open-minded and cautious when interacting with strangers, and not jumping to conclusions based on initial impressions.
          </p>
          <br />
          <p>
            One of the most impactful lessons was the concept of 'truth-default'—the tendency to believe people by default until proven otherwise. It explained why we are often blind to deception in both personal and professional relationships. It’s a powerful reminder to approach interactions with humility and understanding.
          </p>
        </SectionContent>
      </ContentSection>

      <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
    </BookReviewContainer>
  );
};

export default TalkingToStrangers;
